import React, { useState } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import { Link } from 'react-router-dom';
import { CircularProgress, IconButton, Menu, MenuItem } from '@mui/material';
import { useTheme } from 'styled-components';

import { useInfiniteScroll } from 'modules/Core/Hooks';

import {
  Icon,
  LabelWithBackground,
  TablePagination,
  TableEmptyData,
} from 'modules/Core/Common';
import {
  WidgetCard,
  WidgetCardHeader,
  WidgetCardSubHeader,
  WidgetCardContent,
  WidgetEmptyData,
  IntervalOptionsMenu,
} from 'modules/Dashboard/Widgets/Common';
import { formatDatetUTCToTimezone } from 'modules/Utils/Date';
import {
  EProctoringPluginIcon,
  SettingsIcon,
} from '@edusynch/edusynch-svg-icons';
import emptyEventsImg from 'assets/layout/empty-data/widgets-empty-data.svg?url';

import * as S from './MissingVideos.styles';

const I18N_PREFIX = 'modules.widgets.missing_videos';
const MISSING_TYPES = ['webcam', 'screen', 'both'];

const sessionLogPath = (id) => `/antifraud/plugin/log/${id}`;

const getColumns = ({ userDateFormat, userTimeFormat, userTimezone }) => {
  const formatDate = (date) =>
    formatDatetUTCToTimezone(
      date,
      userDateFormat,
      userTimeFormat,
      userTimezone
    );

  return [
    {
      title: i18n.t(`${I18N_PREFIX}.columns.session`),
      key: 'id',
      sort: false,
      render: ({ id }) => (
        <Link
          to={sessionLogPath(id)}
          onClick={(event) => event.stopPropagation()}
        >
          {id}
        </Link>
      ),
    },
    {
      title: i18n.t(`${I18N_PREFIX}.columns.student`),
      key: 'student',
      sort: false,
      render: ({ student }) => (
        <S.Student>
          <span>{student?.name}</span>
          <span>{student?.email}</span>
        </S.Student>
      ),
    },
    {
      title: i18n.t(`${I18N_PREFIX}.columns.missing`),
      key: 'missing_video',
      sort: false,
      render: ({ missing_video: missing }) => (
        <LabelWithBackground
          secondary
          autoSize
          label={i18n.t(`${I18N_PREFIX}.types.${missing}`)}
          style={{ minHeight: 'auto', padding: '0.4rem 1.2rem' }}
        />
      ),
    },
    {
      title: i18n.t(`${I18N_PREFIX}.columns.start`),
      key: 'start_time',
      sort: false,
      render: ({ start_time: startTime }) => (
        <TableEmptyData value={formatDate(startTime)} />
      ),
    },
  ];
};

const TYPE_OPTIONS = ['', ...MISSING_TYPES];

const TypeFilterMenu = ({ value, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();

  const handleSelect = (type) => {
    onChange(type);
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        aria-label="missing-type-filter"
        id="missing-type-filter"
        data-active={!!value}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={
          value
            ? {
                position: 'relative',
                '::before': {
                  content: '""',
                  position: 'absolute',
                  width: '0.8rem',
                  height: '0.8rem',
                  borderRadius: '50%',
                  top: '0.8rem',
                  right: '0.8rem',
                  bgcolor: theme.config.colors.green,
                },
              }
            : undefined
        }
      >
        <Icon name="filter" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        sx={{ '.MuiMenu-list': { padding: 0 } }}
      >
        {TYPE_OPTIONS.map((type) => (
          <MenuItem
            key={type || 'all'}
            selected={value === type}
            onClick={() => handleSelect(type)}
            divider
            sx={{
              fontSize: '1.4rem',
              padding: '1.6rem',
              borderBottomColor: theme.config.colors.gray_100,
            }}
          >
            {i18n.t(`${I18N_PREFIX}.types.${type || 'all'}`)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

TypeFilterMenu.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

export const MissingVideos = ({
  widget,
  isMock,
  onOpenSettings,
  sessions,
  loading,
  hasMore,
  hasError,
  filters,
  authFormat,
  onChangeInterval,
  onChangeType,
  onLoadMore,
  onOpenSession,
}) => {
  const size = widget?.settings?.size || 'large';
  const { lastElementRef } = useInfiniteScroll({
    loading,
    hasMore,
    callback: onLoadMore,
  });

  const renderResults = () => {
    if (hasError) {
      return <S.Error role="alert">{i18n.t(`${I18N_PREFIX}.error`)}</S.Error>;
    }
    if (sessions.length === 0) {
      return (
        <S.EmptyState>
          <WidgetEmptyData
            imageURL={emptyEventsImg}
            title={i18n.t(`${I18N_PREFIX}.empty_data.title`)}
            subtitle={i18n.t(`${I18N_PREFIX}.empty_data.subtitle`)}
          />
        </S.EmptyState>
      );
    }
    return (
      <>
        <TablePagination
          columns={getColumns(authFormat)}
          data={sessions}
          onItemClick={(session) => onOpenSession(session.id)}
        />
        {hasMore && <S.Sentinel ref={lastElementRef} />}
        {loading && sessions.length > 0 && hasMore && (
          <S.LoadingMore data-testid="missing-videos-loading-more">
            <CircularProgress size={20} />
          </S.LoadingMore>
        )}
      </>
    );
  };

  return (
    <WidgetCard>
      <WidgetCardHeader
        size={size}
        Icon={EProctoringPluginIcon}
        title={i18n.t(`${I18N_PREFIX}.title`)}
        subtitle={i18n.t(`${I18N_PREFIX}.subtitle`)}
        showSettingsButton={isMock && size === 'small'}
        onOpenSettings={onOpenSettings}
      >
        <WidgetCardSubHeader size={size}>
          <S.ContentAction>
            {isMock && size === 'large' && (
              <IconButton
                aria-label="settings"
                id="settings"
                onClick={onOpenSettings}
              >
                <SettingsIcon />
              </IconButton>
            )}
            <IntervalOptionsMenu
              defaultOption={filters.interval}
              onChangeOption={onChangeInterval}
            />
            <TypeFilterMenu
              value={filters.missingType}
              onChange={onChangeType}
            />
          </S.ContentAction>
        </WidgetCardSubHeader>
      </WidgetCardHeader>
      <WidgetCardContent size={size} borderTop>
        <S.Body>
          <S.Results
            data-testid="missing-videos-table"
            aria-busy={loading}
            $scrollable={!hasError && sessions.length > 0}
          >
            {renderResults()}
          </S.Results>
        </S.Body>
      </WidgetCardContent>
    </WidgetCard>
  );
};

MissingVideos.propTypes = {
  widget: PropTypes.object,
  isMock: PropTypes.bool,
  onOpenSettings: PropTypes.func,
  sessions: PropTypes.array,
  loading: PropTypes.bool,
  hasMore: PropTypes.bool,
  hasError: PropTypes.bool,
  filters: PropTypes.object.isRequired,
  authFormat: PropTypes.object.isRequired,
  onChangeInterval: PropTypes.func.isRequired,
  onChangeType: PropTypes.func.isRequired,
  onLoadMore: PropTypes.func.isRequired,
  onOpenSession: PropTypes.func.isRequired,
};
